import type { SpriteDefinition } from '@/render/spriteBaker';
import { PALETTE } from '@/sprites/palette';
import type { ToolFamily } from '@/world/toolbelt';

/**
 * Tool icons for the tool button, 16x16 and centred in their grid.
 *
 * Separate from the in-hand overlays on purpose. An overlay is drawn where the
 * tool sits in Katie's grip — off to one side, mostly empty grid — which makes
 * a lopsided icon when blown up to 40px in a button. These are composed to be
 * looked at directly.
 *
 * No new palette entries: trunk brown is already the right wood and stone grey
 * is already the right iron. The palette warns that letters are nearly
 * exhausted, and "iron" that is imperceptibly different from "stone" would be
 * two names for one colour.
 *
 * Keyed by tool *family* rather than item id, so a steel hoe and an iron hoe
 * share an icon and every rod tier looks like a rod.
 */

const HOE: readonly string[] = [
  '................',
  '.............mm.',
  '............mM..',
  '...........mM...',
  '..........mM....',
  '.........mM.....',
  '........mM......',
  '.......mM.......',
  '......mM........',
  '.....mM.........',
  '....mM..........',
  '...mM...........',
  // The head runs across the foot of the shaft rather than hanging below it.
  // A small blob at 24px is a smudge; a broad bar still reads as a hoe.
  '..rrrrrr........',
  '..rRRRRR........',
  '...RRRRR........',
  '................',
];

const WATERING_CAN: readonly string[] = [
  '................',
  '................',
  '................',
  '.rr.............',
  '..rr............',
  '...rr........rr.',
  '...rrrrrrrrrr.r.',
  '...rRRRRRRRRr.r.',
  '...rRRRRRRRRr.r.',
  '...rRRRRRRRRrrr.',
  '...rRRRRRRRRr...',
  '...rRRRRRRRRr...',
  '...rRRRRRRRRr...',
  '...rrrrrrrrrr...',
  '................',
  '................',
];

const ROD: readonly string[] = [
  '..............m.',
  '.............mMw',
  '............mM.w',
  '...........mM..w',
  '..........mM...w',
  '.........mM....w',
  '........mM.....w',
  '.......mM......w',
  '......mM.......w',
  '.....mM........w',
  // A reel on the grip. Without it the rod and the hoe are the same diagonal
  // stick at button size, and the whole point is telling them apart at a glance.
  '..rrmM.........w',
  '..rrM..........w',
  '..MM...........w',
  '..............ww',
  '..............w.',
  '................',
];

const SWORD: readonly string[] = [
  '................',
  '.............rr.',
  '............rr..',
  '...........rr...',
  '..........rr....',
  '.........rr.....',
  '........rr......',
  '.......rr.......',
  '......rr........',
  '.....rr.........',
  '....rR..........',
  '..mmmmm.........',
  '....m...........',
  '....m...........',
  '...mmm..........',
  '................',
];

export const TOOL_ICONS = {
  hoe: { name: 'icon-hoe', rows: HOE, palette: PALETTE },
  wateringCan: { name: 'icon-watering-can', rows: WATERING_CAN, palette: PALETTE },
  rod: { name: 'icon-rod', rows: ROD, palette: PALETTE },
  sword: { name: 'icon-sword', rows: SWORD, palette: PALETTE },
} as const satisfies Record<ToolFamily, SpriteDefinition>;
